import { CalendarCheck, Sigma, Table2, TrendingUp } from 'lucide-react'
import { useId, useMemo, useState, type ReactNode } from 'react'

export interface DailySeries {
  key: string
  label: string
  /** A categorical slot from the validated palette - fixed per entity, never by rank. */
  color: string
  values: number[]
}

const HEIGHT = 240
const PAD = { top: 16, right: 12, bottom: 30, left: 36 }
const GAP = 2 // surface gap between stacked segments
const RADIUS = 5 // rounded data-end, top of each column only
const MAX_BAR = 26 // a sparse month should still read as columns, not slabs

function niceMax(max: number): number {
  if (max <= 4) return 4
  const step = Math.pow(10, Math.floor(Math.log10(max)))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * step >= max) return m * step
  return 10 * step
}

const fmtDay = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })

/** A rect with only its top corners rounded - the end of a column; the base stays square on the axis. */
function topRounded(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`
}

/**
 * Registrations per day as columns, stacked by series when there are several. Hover a day for its
 * numbers; "Show table" lists every day, the view that does not depend on telling colours apart.
 */
export function DailyBarChart({
  title,
  subtitle,
  days,
  series,
  icon,
  accent = 'from-sky-500 to-blue-600',
  categoryBreakdown,
}: {
  title: string
  subtitle: string
  days: string[]
  series: DailySeries[]
  icon?: ReactNode
  /** Tailwind gradient stops for the icon tile and the card's top edge. */
  accent?: string
  /** Per-category values for the same days - shown in the hover tooltip, non-zero only, tallest first. */
  categoryBreakdown?: DailySeries[]
}) {
  const gid = useId().replace(/:/g, '')
  const [hover, setHover] = useState<number | null>(null)
  const [showTable, setShowTable] = useState(false)
  const [width, setWidth] = useState(900)

  const totals = useMemo(() => days.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0)), [days, series])
  const yMax = niceMax(Math.max(0, ...totals))
  const innerW = Math.max(100, width - PAD.left - PAD.right)
  const innerH = HEIGHT - PAD.top - PAD.bottom
  const slot = innerW / Math.max(1, days.length)
  const barW = Math.min(MAX_BAR, Math.max(1, slot - Math.min(6, slot * 0.3)))
  const y = (v: number) => (v / yMax) * innerH
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f))
  const labelEvery = Math.max(1, Math.ceil(days.length / 8))
  const seriesTotals = series.map((s) => s.values.reduce((a, b) => a + b, 0))
  const grand = seriesTotals.reduce((a, b) => a + b, 0)
  const peak = totals.reduce((best, v, i) => (v > totals[best] ? i : best), 0)
  const activeDays = totals.filter((v) => v > 0).length

  return (
    <section className="relative overflow-hidden rounded-2xl bg-white p-5 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} aria-hidden />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}>
            {icon ?? <TrendingUp size={18} />}
          </span>
          <div>
            <h2 className="text-base font-extrabold text-ink-900">{title}</h2>
            <p className="text-sm text-ink-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip icon={<Sigma size={13} />} label="Total" value={grand.toLocaleString()} />
          <Chip
            icon={<TrendingUp size={13} />}
            label="Peak"
            value={totals[peak] > 0 ? `${totals[peak]} on ${fmtDay(days[peak])}` : '-'}
          />
          <Chip icon={<CalendarCheck size={13} />} label="Active days" value={`${activeDays}/${days.length}`} />
        </div>
      </div>

      {series.length > 1 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {series.map((s, i) => (
            <li
              key={s.key}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
              style={{ background: `${s.color}1a`, color: s.color }}
            >
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              {s.label}
              <span className="text-ink-900">{seriesTotals[i].toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}

      <div
        className="relative mt-4"
        ref={(el) => {
          if (el && Math.abs(el.clientWidth - width) > 4) setWidth(el.clientWidth)
        }}
        onMouseLeave={() => setHover(null)}
      >
        <svg width="100%" height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img" aria-label={`${title}: ${subtitle}`}>
          <defs>
            {series.map((s) => (
              <linearGradient key={s.key} id={`${gid}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={1} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0.7} />
              </linearGradient>
            ))}
          </defs>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={0}
                  x2={innerW}
                  y1={innerH - y(t)}
                  y2={innerH - y(t)}
                  stroke="#ebe9e3"
                  strokeWidth={1}
                  strokeDasharray={t === 0 ? undefined : '4 4'}
                />
                <text x={-10} y={innerH - y(t)} dy="0.32em" textAnchor="end" fontSize={12} fontWeight={600} fill="#9a988f">
                  {t}
                </text>
              </g>
            ))}

            {hover !== null && (
              <rect x={hover * slot} y={0} width={slot} height={innerH} rx={4} fill="#0a192f" fillOpacity={0.05} />
            )}

            {days.map((day, i) => {
              const x = i * slot + (slot - barW) / 2
              // Bottom-up, skipping empty series, with a surface gap between the segments.
              const parts = series.map((s) => ({ s, v: s.values[i] ?? 0 })).filter((p) => p.v > 0)
              let base = innerH
              return (
                <g key={day} opacity={hover === null || hover === i ? 1 : 0.5}>
                  {parts.length === 0 && (
                    <circle cx={i * slot + slot / 2} cy={innerH - 3} r={1.5} fill="#d8d5cc" />
                  )}
                  {parts.map((p, j) => {
                    const h = Math.max(2, y(p.v) - (j > 0 ? GAP : 0))
                    const top = base - h
                    const fill = `url(#${gid}-${p.s.key})`
                    const el =
                      j === parts.length - 1 ? (
                        <path key={p.s.key} d={topRounded(x, top, barW, h, RADIUS)} fill={fill} />
                      ) : (
                        <rect key={p.s.key} x={x} y={top} width={barW} height={h} fill={fill} />
                      )
                    base = top - GAP
                    return el
                  })}
                  {totals[i] > 0 && slot >= 14 && (
                    <text x={i * slot + slot / 2} y={innerH - y(totals[i]) - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#1a1812">
                      {totals[i]}
                    </text>
                  )}
                  {i % labelEvery === 0 && (
                    <text x={i * slot + slot / 2} y={innerH + 20} textAnchor="middle" fontSize={12} fontWeight={600} fill="#9a988f">
                      {fmtDay(day)}
                    </text>
                  )}
                  {/* Hit target the full column height - bigger than the mark. */}
                  <rect x={i * slot} y={0} width={slot} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
                </g>
              )
            })}
            <line x1={0} x2={innerW} y1={innerH} y2={innerH} stroke="#cfccc2" strokeWidth={1.5} />
          </g>
        </svg>

        {hover !== null && (
          <div
            className="pointer-events-none absolute top-2 z-10 min-w-[160px] max-w-[220px] rounded-xl bg-deep-900 px-3 py-2 text-xs text-white shadow-float"
            style={{
              left: Math.min(Math.max(PAD.left + hover * slot - 70, 0), width - 220),
            }}
          >
            <div className="font-bold text-sun-400">{fmtDay(days[hover])}</div>
            {/* A category breakdown on a multi-series chart (Providers) replaces the by-kind rows
                with one total - two breakdowns of the same number, by kind and by category, read
                as a mismatch rather than two cuts of the data. Single-series charts (Seekers)
                already show one row, so they are untouched. */}
            {categoryBreakdown && series.length > 1 ? (
              <div className="flex items-center justify-between gap-3 text-cream-300">
                <span>Total</span>
                <span className="font-bold tabular-nums text-white">{totals[hover]}</span>
              </div>
            ) : (
              series.map((s) => (
                <div key={s.key} className="mt-1 flex items-center justify-between gap-3 text-cream-300">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="font-bold tabular-nums text-white">{s.values[hover] ?? 0}</span>
                </div>
              ))
            )}
            {series.length > 1 && !categoryBreakdown && (
              <div className="mt-1 flex justify-between border-t border-white/15 pt-1 text-cream-300">
                Total <span className="font-bold tabular-nums text-white">{totals[hover]}</span>
              </div>
            )}
            {categoryBreakdown && (() => {
              const byCategory = categoryBreakdown
                .map((c) => ({ ...c, v: c.values[hover] ?? 0 }))
                .filter((c) => c.v > 0)
                .sort((a, b) => b.v - a.v)
              return (
                byCategory.length > 0 && (
                  <div className="mt-2 border-t border-white/15 pt-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-cream-300/70">By category</div>
                    {byCategory.map((c) => (
                      <div key={c.key} className="mt-1 flex items-center justify-between gap-3 text-cream-300">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: c.color }} />
                          <span className="truncate">{c.label}</span>
                        </span>
                        <span className="shrink-0 font-bold tabular-nums text-white">{c.v}</span>
                      </div>
                    ))}
                  </div>
                )
              )
            })()}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3 text-xs text-ink-500">
        <span>
          <span className="font-bold text-ink-900">{grand.toLocaleString()}</span> in these {days.length} days
        </span>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 px-3 py-1 font-semibold text-ink-700 transition hover:bg-ink-100 hover:text-ink-900"
        >
          <Table2 size={13} /> {showTable ? 'Hide table' : 'Show table'}
        </button>
      </div>

      {showTable && (
        <div className="mt-3 max-h-72 overflow-auto rounded-xl border border-ink-100">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-ink-50 text-ink-500">
              <tr>
                <th className="px-3 py-2 font-semibold">Day</th>
                {series.map((s) => (
                  <th key={s.key} className="px-3 py-2 text-right font-semibold">
                    {s.label}
                  </th>
                ))}
                {series.length > 1 && <th className="px-3 py-2 text-right font-semibold">Total</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {[...days.keys()].reverse().map((i) => (
                <tr key={days[i]}>
                  <td className="px-3 py-1.5 text-ink-600">{fmtDay(days[i])}</td>
                  {series.map((s) => (
                    <td key={s.key} className="px-3 py-1.5 text-right tabular-nums text-ink-900">
                      {s.values[i] ?? 0}
                    </td>
                  ))}
                  {series.length > 1 && <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{totals[i]}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Chip({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-ink-50 px-2.5 py-1.5 text-xs ring-1 ring-ink-100">
      <span className="text-ink-400">{icon}</span>
      <span className="text-ink-500">{label}</span>
      <span className="font-bold text-ink-900">{value}</span>
    </span>
  )
}
